import ApiService from '@/common/api.service'

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param photo Object
 *
 * @return Object
 */
function transform (photo) {
  return {
    id: photo.id,
    filename: photo.filename,
    size_bytes: photo.size_bytes,
    type: photo.type,
    url: photo.url
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
export function defaults () {
  return {
    isEditing: false,
    isAdding: false,
    isDeleting: false,
    warning: '',
    photos: []
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns an array of photos
   *
   * @return Array
   */
  photos (state) {
    return state.photos
  },

  /**
   * Returns the current form warning.
   *
   * @param state Object
   *
   * @return string
   */
  warning (state) {
    return state.warning
  }
}

export const actions = {
  /**
   * API request to create a new photo. The payload must be in the format:
   * { url: String, data: Object }
   *
   * @return Promise
   */
  post (context, payload) {
    return ApiService
      .post(payload.url, transform(payload.data))
      .then((response) => {
        context.commit('add', response.data.data)
        return response
      })
  },

  /**
   * API request to create multiple new photos. The payload must be in the format:
   * { url: String, data: Array }
   *
   * @return Promise
   */
  postMultiple (context, payload) {
    const photos = payload.data.map(photo => transform(photo))

    return ApiService
      .post(payload.url, { photos: photos })
      .then((response) => {
        context.commit('addMultiple', response.data.data)
        return response
      })
  },

  /**
   * API request to delete an existing photo. The payload must be in the format:
   * { url: String, data: Object  }
   *
   * @return Promise
   */
  delete (context, payload) {
    return ApiService
      .delete(payload.url)
      .then((response) => {
        context.commit('removeById', payload.data.id)
        return response
      })
  }
}

export const mutations = {
  /**
   * Set an array of photos.
   *
   * @param state       Object
   * @param photos Array
   */
  setPhotos (state, photos) {
    state.photos = photos
  },

  /**
   * Add a new photo.
   *
   * @param state      Object
   * @param photo Object
   */
  add (state, photo) {
    state.photos.push(photo)
  },

  /**
   * Add multiple new photos.
   *
   * @param state  Object
   * @param photos Array
   */
  addMultiple (state, photos) {
    photos.forEach(photo => state.photos.push(photo))
  },

  /**
   * Find a photo by index in the array of photos and updates it.
   *
   * @param state   Object
   * @param photo Object
   * @param index   Number
   */
  updateByIndex (state, {photo, index}) {
    state.photos[index] = photo
  },

  /**
   * Updates the last photo that was added.
   *
   * @param state   Object
   * @param photo Object
   */
  updateNewest (state, photo) {
    state.photos[state.photos.length - 1] = photo
  },

  /**
   * Find a photo by id and remove it.
   *
   * @param state Object
   * @param id    Number
   */
  removeById (state, id) {
    let index = state.photos.findIndex(element => element.id === id)
    state.photos.splice(index, 1)
  },

  /**
   * Remove a photo by index in the array of photos.
   *
   * @param state Object
   * @param index Number
   */
  removeByIndex (state, index) {
    state.photos.splice(index, 1)
  },

  /**
   * Clears the photos from the state.
   *
   * @param state Object
   */
  purge (state) {
    state.photos = []
  },

  /**
   * Sets the isAdding form state.
   *
   * @param state   Object
   * @param isAdding Boolean
   */
  setIsAdding (state, isAdding) {
    state.isAdding = isAdding
  },

  /**
   * Sets the isEditing form state.
   *
   * @param state     Object
   * @param isEditing Boolean
   */
  setIsEditing (state, isEditing) {
    state.isEditing = isEditing
  },

  /**
   * Sets the isDeleting form state.
   *
   * @param state      Object
   * @param isDeleting Boolean
   */
  setIsDeleting (state, isDeleting) {
    state.isDeleting = isDeleting
  },

  /**
   * Sets the form warning.
   *
   * @param state      Object
   * @param warning Boolean
   */
  setWarning (state, warning) {
    state.warning = warning
  },

  /**
   * Resets the form's state.
   *
   * @param state      Object
   */
  resetFormState (state) {
    state.isAdding = false
    state.isEditing = false
    state.isDeleting = false
    state.warning = ''
  }
}

export default {
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/photos/photosModule.js